import { useMemo } from "react";
import { useListDataStreams } from "@/api/orval/generated/data-stream";
import type { ListDataStreamsParams } from "@/api/orval/generated/schemas";
import { type DashboardSpec, type PanelSpec, SPEC_VERSION } from "../ChartSpec";
import { DEFAULT_RELATIVE_DURATION } from "../time-window/time-range-slice";
import { buildAllStreamsPanel, buildCategoryPanels, toStreamEntry } from "./panel-builders";
import { type ResolvedStream, resolveStreams } from "./resolve-streams";

const DEFAULT_TIME_RANGE = { mode: "relative", duration: DEFAULT_RELATIVE_DURATION } as const;

/** "single" is every stream on one panel; "stacked" is one panel per unit category. */
export type PanelLayout = "single" | "stacked";

export type DataViewerSpecResult = {
    spec: DashboardSpec;
    isLoading: boolean;
    isError: boolean;
    isEmpty: boolean;
};

function toSpec(panels: PanelSpec[]): DashboardSpec {
    return { version: SPEC_VERSION, defaultTimeRange: DEFAULT_TIME_RANGE, panels };
}

export function getZoneSpec(resolvedStreams: ResolvedStream[], layout: PanelLayout): DashboardSpec {
    const entries = resolvedStreams.map(toStreamEntry);

    if (layout === "stacked") return toSpec(buildCategoryPanels(entries));

    const panel = buildAllStreamsPanel(entries);
    return toSpec(panel ? [panel] : []);
}

export function getDeviceSpec(resolvedStreams: ResolvedStream[]): DashboardSpec {
    return toSpec(buildCategoryPanels(resolvedStreams.map(toStreamEntry)));
}

function useResolvedStreams(params: ListDataStreamsParams, enabled: boolean) {
    const { data, isLoading, isError } = useListDataStreams(params, { query: { enabled } });
    const resolvedStreams: ResolvedStream[] = useMemo(() => (data ? resolveStreams(data) : []), [data]);

    return {
        resolvedStreams,
        isLoading,
        isError,
        isEmpty: !isLoading && !isError && resolvedStreams.length === 0,
    };
}

/** Think of this as the configuration file for how a zone-based DataViewer should be rendered. */
export function useZoneDataViewerSpec(zoneId: string, layout: PanelLayout): DataViewerSpecResult {
    const { resolvedStreams, ...status } = useResolvedStreams({ zoneId }, !!zoneId);

    // SpecProvider replaces the document when initialSpec's reference changes — keep it stable.
    const spec = useMemo(() => getZoneSpec(resolvedStreams, layout), [resolvedStreams, layout]);

    return { spec, ...status };
}

/**
 * Think of this as the configuration file for how a device-based DataViewer should be rendered.
 *
 * @param host - The host device that owns the streams. The streams API rejects a device id without its
 *     collection, so pass both or nothing.
 */
export function useDeviceDataViewerSpec(host: { id: string; collection: string } | undefined): DataViewerSpecResult {
    const { resolvedStreams, ...status } = useResolvedStreams(
        { deviceId: host?.id, deviceCollection: host?.collection },
        !!host,
    );

    // SpecProvider replaces the document when initialSpec's reference changes — keep it stable.
    const spec = useMemo(() => getDeviceSpec(resolvedStreams), [resolvedStreams]);

    return { spec, ...status };
}
